import { useState } from 'react';
import { SidebarTrigger } from '../../components/ui/sidebar';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useFeatureFlag } from '../../services/featureFlag.service';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Loader2, Download, Layers, Trash2, ArrowUpCircle, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { useAllTemplates, useTemplateInstallations } from '../../hooks/useTemplates';
import { templateService } from '../../services/template.service';

export default function InstallTemplates() {
  const { data: templates, isLoading } = useAllTemplates();
  const { data: installations } = useTemplateInstallations();
  const queryClient = useQueryClient();
  const isTemplateManagementEnabled = useFeatureFlag('template-management', false);
  const [installingId, setInstallingId] = useState<string | null>(null);
  const [uninstallingId, setUninstallingId] = useState<string | null>(null);
  const [upgradingId, setUpgradingId] = useState<string | null>(null);

  const installedTemplateIds = new Set(installations?.map(i => i.template_id) || []);

  const handleUpgrade = async (installationId: string) => {
    setUpgradingId(installationId);
    try {
      await templateService.upgradeInstallation(installationId);
      queryClient.invalidateQueries({ queryKey: ['template-installations'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      toast.success('Template upgraded successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to upgrade template');
    } finally {
      setUpgradingId(null);
    }
  };

  const handleUninstall = async (templateId: string, templateName: string) => {
    if (!confirm(`Are you sure you want to uninstall "${templateName}"? All template-managed nodes and fields will be unlocked.`)) {
      return;
    }
    setUninstallingId(templateId);
    try {
      await templateService.uninstallTemplate(templateId);
      queryClient.invalidateQueries({ queryKey: ['template-installations'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      toast.success('Template uninstalled successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to uninstall template');
    } finally {
      setUninstallingId(null);
    }
  };

  const handleInstall = async (templateId: string) => {
    setInstallingId(templateId);
    try {
      await templateService.installTemplate(templateId);
      queryClient.invalidateQueries({ queryKey: ['template-installations'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-nodes'] });
      toast.success('Template installed successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to install template');
    } finally {
      setInstallingId(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SidebarTrigger className="md:hidden h-5 w-5" />
            <Layers className="h-6 w-6 text-muted-foreground" />
            <div>
              <h1 className="text-2xl font-semibold">Templates</h1>
              <p className="text-sm text-muted-foreground">Browse and install available templates</p>
            </div>
          </div>
          {isTemplateManagementEnabled && (
            <Button variant="outline" size="sm" asChild>
              <Link to="/templates/admin">
                <Settings className="h-4 w-4 mr-2" />
                Template Administration
              </Link>
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !templates?.length ? (
        <div className="text-center py-12 text-muted-foreground">
          No templates available.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Version</TableHead>
              <TableHead className="w-[120px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.map((template) => {
              const isInstalled = installedTemplateIds.has(template.id);
              return (
                <TableRow key={template.id}>
                  <TableCell className="font-medium">{template.name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{template.type}</Badge>
                  </TableCell>
                  <TableCell>{template.version}</TableCell>
                  <TableCell>
                    {isInstalled ? (
                      <Badge variant="outline">Installed</Badge>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleInstall(template.id)}
                        disabled={installingId === template.id}
                      >
                        {installingId === template.id ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <Download className="h-4 w-4 mr-1" />
                        )}
                        Install
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Installed Templates Section */}
      {installations && installations.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Installed Templates</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Installed Version</TableHead>
                <TableHead>Latest Version</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {installations.map((installation) => {
                const hasUpdate = installation.latest_version < installation.template.version;
                return (
                  <TableRow key={installation.id}>
                    <TableCell className="font-medium">{installation.template.name}</TableCell>
                    <TableCell>{installation.latest_version}</TableCell>
                    <TableCell>{installation.template.version}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {hasUpdate ? (
                          <Button
                            size="sm"
                            onClick={() => handleUpgrade(installation.id)}
                            disabled={upgradingId === installation.id}
                          >
                            {upgradingId === installation.id ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <ArrowUpCircle className="h-4 w-4 mr-1" />
                            )}
                            Update
                          </Button>
                        ) : (
                          <>
                            <Badge variant="secondary">Up to date</Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUpgrade(installation.id)}
                              disabled={upgradingId === installation.id}
                            >
                              {upgradingId === installation.id ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              ) : (
                                <ArrowUpCircle className="h-4 w-4 mr-1" />
                              )}
                              Reinstall
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUninstall(installation.template_id, installation.template.name)}
                          disabled={uninstallingId === installation.template_id}
                          className="text-destructive hover:text-destructive"
                        >
                          {uninstallingId === installation.template_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                          Uninstall
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
